import { groupSamePhrase } from '@orborix/shared-types';
import type { CoachReviewItem, ReviewResult } from './useCoachReview.js';

/**
 * Dos tarjetas que, con la frase entera del server (`forms` de
 * `/corrections/review-cues`), terminan en la misma frase se repasan UNA vez
 * (Emmanuel, 20/10: «las que terminan en la misma frase, una tarjeta»). Antes
 * de la frase entera ya se juntaban por el pedazo corregido; con ella, dos
 * correcciones distintas de la misma oración («I go → I went» y «to park → to
 * the park» en «Yesterday I go to park.») darían la misma tarjeta dos veces
 * (card #571). También las que son casi la misma frase (card #595, `nearPhraseKey`).
 * El Repaso del día ya llega junto del server (`/corrections/due`); esto cubre el
 * cierre, la partida y el Radar, y lo que cambia con la frase de la pista.
 *
 * La que queda lleva los miembros de todas (lo decidido se guarda para todas) y
 * `absorbed` dice cuáles absorbió cada una, para devolver su resultado también
 * (`spreadResults`): quien abrió el repaso sigue viendo una por cada tarjeta que pasó.
 */
export function mergeSamePhrase<T extends Omit<CoachReviewItem, 'cue' | 'hint'>>(
  items: ReadonlyArray<T>,
): { items: T[]; absorbed: Map<string, T[]> } {
  const absorbed = new Map<string, T[]>();
  // Por la frase o CASI la misma (card #595); queda la más larga (`groupSamePhrase`).
  const out = groupSamePhrase(items, (item) => item.corrected).map(([keep, ...rest]) => {
    if (rest.length === 0) return keep!;
    absorbed.set(keep!.id, rest);
    const radar = keep!.radar ?? rest.find((r) => r.radar)?.radar;
    return {
      ...keep!,
      memberIds: [...keep!.memberIds, ...rest.flatMap((r) => r.memberIds)],
      ...(radar ? { radar } : {}),
    };
  });
  return { items: out, absorbed };
}

/** Los resultados del repaso, con uno igual para cada tarjeta que absorbió otra. */
export function spreadResults(
  results: ReadonlyArray<ReviewResult>,
  absorbed: ReadonlyMap<string, ReadonlyArray<Omit<CoachReviewItem, 'cue' | 'hint'>>>,
): ReviewResult[] {
  return results.flatMap((r) => [
    r,
    ...(absorbed.get(r.item.id) ?? []).map((a) => ({
      ...r,
      item: { ...r.item, ...a, cue: r.item.cue, hint: r.item.hint },
    })),
  ]);
}
